<template>
  <ProgressModal
    v-if="isLoadingAction"
    :processedItems="processedItems"
    :progressIndicator="progressIndicator"
    :totalItems="freebetsSelected.length"
    title="Please wait..."
  />

  <div
    v-if="!betId"
    :class="`content-wrapper ${isMenuOpen ? 'menu-open' : 'menu-closed'}`"
    id="freebets-table"
  >
    <div class="content content-styles text-slate-800 ov-visible md:mx-[1rem]">
      <!-- Header & Description -->
      <div class="mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-5 rounded-3xl border border-slate-200 shadow-sm">
        <div>
          <div class="flex items-center gap-2 mb-1.5 flex-wrap">
            <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <i class="fa-solid fa-ticket mr-1 text-emerald-500"></i> Freebet Center
            </span>
            <span class="text-xs text-slate-500 font-medium">
              Standalone & Claimed Rewards
            </span>
          </div>
          <h1 class="text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            Freebets Management
          </h1>
          <p class="text-xs text-slate-500 mt-1 max-w-3xl leading-relaxed">
            Here you can monitor and manage <strong>standalone freebets</strong> created directly for specific players, as well as <strong>claimed freebets</strong> awarded automatically through <router-link :to="{ name: 'Promotions' }" class="text-indigo-600 hover:underline font-semibold">Promotions Hub</router-link> campaigns (Tournaments, Races, Missions, Chat Rain, and Mystery Drops). Use the <em>Origin</em> filter below to filter between standalone allocations and campaign-driven rewards.
          </p>

          <!-- Context Badges -->
          <div class="mt-3 flex items-center gap-2 flex-wrap text-xs">
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-slate-50 border border-slate-200 text-slate-700">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
              <strong>Standalone:</strong> Direct allocations with dedicated quota & limits
            </span>
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-slate-50 border border-slate-200 text-slate-700">
              <span class="w-2 h-2 rounded-full bg-indigo-500"></span>
              <strong>Promotions Claim:</strong> Automatically unlocked from Promotions Hub events
            </span>
          </div>
        </div>

        <!-- Create Button -->
        <div class="flex-shrink-0">
          <router-link
            :to="{
              name: 'FreebetsCreate',
              params: {
                lang: $route.params.lang || 'en',
                ...($route.params.skinFilt || skinStorage
                  ? {
                      skinFilt: (
                        $route.params.skinFilt || skinStorage
                      ).toString(),
                    }
                  : {}),
              },
            }"
            class="text-teal-900 btn create-freebet bg-teal-100 hover:bg-teal-200 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors duration-150 active:scale-95 border border-teal-200 shadow-sm font-bold text-sm"
          >
            <i class="fa-solid fa-plus text-base text-teal-700"></i>
            <span class="text font-bold text-teal-900 tracking-tight">
              {{ $t("util.create") || "Create Freebet" }}
            </span>
          </router-link>
        </div>
      </div>

      <div
        class="box content-box-style large ov-hidd p-0 min-h-[100dvh] space-y-3"
      >
        <Filters
          :options="options"
          :resetFilter="resetFilter"
          :resetPagination="resetPagination"
          :search="search"
          :showActionButton="true"
        />

        <Transition name="fade-slide">
          <div v-if="selectionCount > 0" class="manage-freebets-status mb-6">
            <div
              class="flex items-center gap-2 p-2 bg-slate-100/50 rounded-2xl overflow-x-auto"
            >
              <button
                v-for="(btn, index) in freebetsButton"
                :key="index"
                @click="btn.action()"
                :class="[
                  'inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm transition-all duration-200 active:scale-95 shadow-sm',
                  btn.name === 'Enable'
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                    : '',
                  btn.name === 'Disable'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200'
                    : '',
                  btn.name === 'Remove'
                    ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'
                    : '',
                ]"
              >
                <span>{{ btn.name }}</span>
                <span class="opacity-60 text-xs">({{ selectionCount }})</span>
                <i :class="`fas fa-${btn.icon} text-xs`"></i>
              </button>
            </div>
          </div>
        </Transition>

        <div
          v-if="isLoading"
          class="flex-grow flex flex-col items-center justify-center p-20"
        >
          <Loader />
        </div>

        <div
          v-else
          class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
        >
          <DataTable
            v-model:selection="freebetsSelected"
            :value="tableData"
            @row-select="onRowSelect"
            @row-unselect="onRowUnselect"
            @row-select-all="onRowSelectAll"
            @row-unselect-all="onRowUnselectAll"
            :loading="isLoading"
            :rows="selectedLimit"
            :totalRecords="totalElements"
            :rowsPerPageOptions="limitValues"
            :lazy="true"
            :paginator="true"
            dataKey="_id"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :currentPageReportTemplate="`Showing {first} to {last} of {totalRecords} freebets`"
            showCurrentPageReport
            stripedRows
            removableSort
            scrollable
            scrollHeight="flex"
            class="freebets-datatable"
            @page="onPage"
            @sort="onSort"
            :sortField="sortField"
            :sortOrder="sortOrder === 'desc' ? -1 : 1"
            :first="(currentPage - 1) * selectedLimit"
          >
            <Column selectionMode="multiple" headerStyle="width: 1rem" />

            <Column field="external_id" header="External ID" sortable />

            <Column field="campaign_id" header="Origin / Campaign" sortable>
              <template #body="{ data }">
                <router-link
                  v-if="data.campaign_id"
                  :to="'/slot/promotions/' + data.campaign_id"
                  class="clickable-cell text-indigo-600 hover:underline inline-flex items-center gap-1 font-semibold"
                >
                  <i class="fa-solid fa-bullhorn text-xs text-indigo-500"></i>
                  <span>{{ data.campaign_id }}</span>
                </router-link>
                <span
                  v-else
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                >
                  <i class="fa-solid fa-ticket text-[10px]"></i> Standalone
                </span>
              </template>
            </Column>

            <Column field="title" header="Title" sortable />

            <Column field="skin_id" header="Skin ID" sortable />

            <Column field="is_enabled" header="Enabled" sortable>
              <template #body="{ data }">
                <span :class="data.is_enabled ? 'text-success' : 'text-danger'">
                  <i
                    :class="`fas fa-${
                      data.is_enabled ? 'check-circle' : 'times-circle'
                    }`"
                  ></i>
                  {{ data.is_enabled ? "Enabled" : "Disabled" }}
                </span>
              </template>
            </Column>

            <Column field="max_players" header="Max Players" sortable />

            <Column field="mode" header="Mode" sortable>
              <template #body="{ data }">
                {{ formatMode(data.mode) }}
              </template>
            </Column>

            <Column field="players_count" header="Active Players" sortable />

            <Column field="quantity" header="Quantity" sortable />

            <Column
              field="freebetcurrencies_length"
              header="Currencies"
              sortable
            />

            <Column field="start_at" header="Start Time" sortable>
              <template #body="{ data }">
                {{ formatDate(data.start_at) }}
              </template>
            </Column>

            <Column field="end_at" header="End Time" sortable>
              <template #body="{ data }">
                {{ formatDate(data.end_at) }}
              </template>
            </Column>

            <Column
              header="Actions"
              :exportable="false"
              style="min-width: 150px"
            >
              <template #body="{ data }">
                <div class="flex items-center gap-1">
                  <router-link
                    :to="{
                      name: 'Freebets',
                      params: {
                        id: data.external_id,
                        lang: $route.params.lang || 'en',
                        ...($route.params.skinFilt ||
                        skinStorage ||
                        data.skin_id
                          ? {
                              skinFilt: (
                                $route.params.skinFilt ||
                                skinStorage ||
                                data.skin_id
                              ).toString(),
                            }
                          : {}),
                      },
                    }"
                  >
                    <Button
                      icon="pi pi-eye"
                      rounded
                      text
                      severity="info"
                      v-tooltip.top="'View Detail'"
                      class="hover:bg-blue-50 rounded-full"
                    />
                  </router-link>

                  <router-link
                    :to="{
                      name: 'FreebetsEdit',
                      params: {
                        id: data.external_id,
                        lang: $route.params.lang || 'en',
                        ...($route.params.skinFilt ||
                        skinStorage ||
                        data.skin_id
                          ? {
                              skinFilt: (
                                $route.params.skinFilt ||
                                skinStorage ||
                                data.skin_id
                              ).toString(),
                            }
                          : {}),
                      },
                    }"
                  >
                    <Button
                      icon="pi pi-pencil"
                      rounded
                      text
                      severity="warning"
                      v-tooltip.top="'Edit Freebet'"
                      class="hover:bg-amber-50 rounded-full"
                    />
                  </router-link>

                  <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    severity="danger"
                    v-tooltip.top="'Remove Freebet'"
                    @click="removeFreebets(data)"
                    class="hover:bg-red-50 rounded-full"
                  />
                </div>
              </template>
            </Column>

            <template #empty>
              <div class="text-center py-4 text-muted">No freebets found.</div>
            </template>

            <template #loading>
              <div class="text-center py-4">Loading freebets...</div>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </div>

  <div
    v-else
    class="h-auto"
    :class="`content-wrapper ${isMenuOpen ? 'menu-open' : 'menu-closed'}`"
    id="freebet-table-details"
  >
    <div class="flex items-center justify-start mb-3 md:px-3">
      <div
        class="inline-flex p-1.5 bg-gray-100/80 rounded-2xl border border-gray-200/50 shadow-inner backdrop-blur-md overflow-x-auto"
      >
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="tabSelected = tab.value"
          :class="[
            'relative px-3 py-2.5 text-[11px] whitespace-nowrap font-semibold uppercase tracking-widest transition-all duration-300 rounded-xl flex items-center gap-2 group',
            tabSelected === tab.value
              ? 'bg-white text-[#386f99] shadow-[0_4px_12px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.03]'
              : 'text-slate-600 hover:text-slate-500 hover:bg-white/40',
          ]"
        >
          <div class="relative flex items-center justify-center">
            <span
              v-if="tabSelected === tab.value"
              class="absolute w-2 h-2 rounded-full bg-[#386f99]"
            ></span>
            <span
              :class="[
                'relative w-1.5 h-1.5 rounded-full transition-all duration-300',
                tabSelected === tab.value
                  ? 'bg-[#386f99] scale-110'
                  : 'bg-gray-300 group-hover:bg-gray-400',
              ]"
            ></span>
          </div>

          <span class="relative">{{ tab.label }}</span>
        </button>
      </div>
    </div>
    <FreebetInfo
      v-if="tabSelected === 'freebets_info'"
      :betId="betId"
      :detail="detail"
      :isLoadingDetail="isLoading"
      :saveDetail="saveDetail"
    />

    <FreebetRounds
      v-if="tabSelected === 'rounds_info'"
      :freebet_id="freebet_id"
      :skinFilt="detail.skin_id"
    />

    <FreebetPlayersActive
      v-if="tabSelected === 'freebets_player_active'"
      :freebet_id="freebet_id"
      :skinFilt="freebet_skin_id"
    />

    <FreebetRestriction
      v-if="tabSelected === 'freebets_restrictions'"
      :detail="detailsForRestrictions"
      :freebet_id="freebet_id"
      :skinFilt="freebet_skin_id"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Loader from "../components/Loader.vue";
import Filters from "../components/table/Filters.vue";
import ProgressModal from "../components/Modal/ProgressModal.vue";
import FreebetInfo from "../components/FreebetsComponent/FreebetInfo.vue";
import FreebetRounds from "../components/FreebetsComponent/FreebetRounds.vue";
import RoundFinder from "../components/round/RoundFinder.vue";
import FreebetPlayersActive from "../components/FreebetsComponent/FreebetPlayersActive.vue";
import FreebetRestriction from "../components/FreebetsComponent/FreebetRestriction.vue";
import { getFetch, putFetch, deleteRequest } from "@/api/axiosClient";
import { formatString } from "@/helpers/date";

export default {
  name: "FreebetsPage",

  components: {
    DataTable,
    Column,
    Button,
    ProgressModal,
    Loader,
    Filters,
    FreebetInfo,
    FreebetRounds,
    RoundFinder,
    FreebetPlayersActive,
    FreebetRestriction,
  },

  data() {
    return {
      filtersRound: [
        {
          label: "Round ID",
          type: "text",
          placeholder: "Search for Round ID",
          property: "round_id",
        },
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
        },
        {
          label: "Bet Type",
          type: "select",
          placeholder: "Choose Bet Type",
          property: "type_id",
          options: ["All", "Cashout 1", "Cashout 2", "Even/Odd", "Ranges"],
        },
        {
          label: "Bet ID",
          type: "text",
          placeholder: "Search for Bet ID",
          property: "bet_id",
        },
        {
          label: "Freebet ID",
          type: "text",
          placeholder: "Search for Freebet ID",
          property: "freebet_id",
          disabled: true,
          default: "",
        },
      ],
      freebetsButton: [
        {
          name: "Enable",
          label: "enable",
          icon: "circle-check",
          action: () => this.changeStatus("enable"),
          btnType:
            "px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors",
        },
        {
          name: "Disable",
          label: "disable",
          icon: "circle-xmark",
          action: () => this.changeStatus("disable"),
          btnType:
            "px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors",
        },
        {
          name: "Remove",
          label: "remove",
          icon: "trash",
          action: () => this.removeFreebets(),
          btnType:
            "px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors",
        },
      ],
      freebet_id: "",
      freebet_skin_id: "",
      external_id: null,
      game_id: "All",
      freebet_type: "All",
      is_enabled: "All",
      has_campaign: "All",
      currentPage: 1,
      totalPages: 1,
      totalElements: 0,
      sortField: null,
      sortOrder: null,
      limit: [
        { label: "15", value: 15, selected: false },
        { label: "25", value: 25, selected: false },
        { label: "50", value: 50, selected: true },
        { label: "100", value: 100, selected: false },
      ],
      start_date: null,
      end_date: null,
      tableData: [],
      tabSelected: "freebets_info",
      tabs: [
        { label: "Details", value: "freebets_info", selected: true },
        { label: "Rounds", value: "rounds_info", selected: false },
        {
          label: "Freebets Player Active",
          value: "freebets_player_active",
          selected: false,
        },
        {
          label: "Freebets Restriction",
          value: "freebets_restrictions",
          selected: false,
        },
      ],
      detail: [],
      detailsForRestrictions: {},
      isLoading: false,
      isLoadingAction: false,
      progressIndicator: 0,
      processedItems: 0,
      showModal: false,
      freebetsSelected: [],
      options: [
        {
          label: "External ID",
          type: "text",
          placeholder: "Search for External ID",
          property: "external_id",
        },
        {
          label: "Start Date",
          type: "datetime-local",
          placeholder: "Start Date",
          property: "start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          placeholder: "End Date",
          property: "end_date",
          isDateTime: true,
        },
        {
          label: "Enabled",
          type: "select",
          placeholder: "Enabled",
          property: "is_enabled",
          options: ["All", "True", "False"],
        },
        {
          label: "Origin",
          type: "select",
          placeholder: "Origin",
          property: "has_campaign",
          customLabelForOptions: ["All", "By campaign", "Standard"],
          options: ["All", "True", "False"],
        },
      ],
    };
  },

  computed: {
    ...mapGetters([
      "isMenuOpen",
      "endpoint",
      "crashGameOptions",
      "plinkoGameOptions",
      "gameIdTheEar",
    ]),

    betId() {
      return this.$route.params.id;
    },

    selectedLimit() {
      return this.limit.find((l) => l.selected)?.value ?? 50;
    },

    limitValues() {
      return this.limit.map((l) => l.value);
    },

    isTheearcasino() {
      return "theearcasino";
    },

    selectionCount() {
      return this.freebetsSelected.length;
    },

    startDateMax() {
      return this.end_date || new Date().toISOString().split("T")[0];
    },

    endDateMin() {
      return this.start_date || new Date(0).toISOString().split("T")[0];
    },
  },

  watch: {
    betId(val) {
      val ? null : this.search();
    },
    external_id() {
      this.updateQueryString();
    },
    freebet_type() {
      this.updateQueryString();
    },
    start_date() {
      this.updateQueryString();
    },
    end_date() {
      this.updateQueryString();
    },
    is_enabled() {
      this.updateQueryString();
    },
    has_campaign() {
      this.updateQueryString();
    },
  },

  methods: {
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleString();
    },

    formatMode(val) {
      return val ?? "—";
    },

    formatType(val) {
      const map = { ...this.crashGameOptions, ...this.plinkoGameOptions };
      return Object.values(map).find((v) => v === val) ?? val ?? "—";
    },

    setTypeOptionsBasedOnGameType() {
      let typeOptions = [];

      if (this.isTheearcasino) {
        typeOptions = Object.values(this.plinkoGameOptions);
        const gameValues = Object.values(this.gameIdTheEar);
        const idx = this.options.findIndex((o) => o.property === "game_id");
        if (idx === -1) {
          this.options.push({
            label: "Game ID",
            type: "select",
            placeholder: "Choose Game ID",
            property: "game_id",
            options: ["All", ...gameValues],
          });
        } else {
          this.options[idx].options = ["All", ...gameValues];
        }
      } else {
        typeOptions = Object.values(this.crashGameOptions);
        const idx = this.options.findIndex((o) => o.property === "game_id");
        if (idx !== -1) this.options.splice(idx, 1);
      }

      const typeIdx = this.options.findIndex(
        (o) => o.property === "freebet_type",
      );
      if (typeIdx !== -1) this.options[typeIdx].options = [...typeOptions];
    },

    goBack() {
      const skin = this.$route.params.skinFilt || this.skinStorage;
      this.$router.push({
        name: "Freebets",
        params: {
          lang: this.$route.params.lang || "en",
          ...(skin ? { skinFilt: skin.toString() } : {}),
        },
      });
    },
    updateQueryString() {
      const query = {};

      if (this.external_id) {
        query.external_id = this.external_id;
        const idx = this.filtersRound.findIndex(
          (f) => f.property === "freebet_id",
        );
        if (idx !== -1) this.filtersRound[idx].default = this.external_id;
      }
      if (this.freebet_type !== "All") query.freebet_type = this.freebet_type;
      if (this.start_date) query.start_date = this.start_date;
      if (this.end_date) query.end_date = this.end_date;
      if (this.is_enabled && this.is_enabled !== "All")
        query.is_enabled = this.is_enabled === "True" ? "true" : "false";
      if (this.has_campaign && this.has_campaign !== "All")
        query.has_campaign = this.has_campaign === "True" ? "true" : "false";

      if (this.sortField) {
        query.sortField = this.sortField;
        query.sortOrder = this.sortOrder;
      } else {
        delete query.sortField;
        delete query.sortOrder;
      }

      this.$router.replace({ query });
    },
    checkIsSelected(row) {
      return this.freebetsSelected.some(
        (f) =>
          f._id === row._id &&
          f.external_id === row.external_id &&
          f.type === row.type &&
          f.start_at === row.start_at,
      );
    },
    async changeStatus(action) {
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: `Do you want to ${action} all the ${this.freebetsSelected.length} freebet selected?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      const total = this.freebetsSelected.length;
      try {
        this.isLoadingAction = true;
        document.body.style.overflow = "hidden";

        for (let i = 0; i < total; i++) {
          const id = this.freebetsSelected[i]._id;
          this.processedItems = i + 1;
          await putFetch(
            `/freebets/${id}`,
            {
              action,
              is_enabled: action === "enable",
            },
            true,
          );
          this.progressIndicator = Math.round(((i + 1) / total) * 100);
        }

        this.progressIndicator = 100;
        this.freebetsSelected = [];
        setTimeout(async () => {
          this.isLoadingAction = false;
          await Swal.fire("Complete", "Element status updated!");
          window.location.reload();
        }, 600);
      } catch (err) {
        console.error("Error updating element:", err);
        Swal.fire("Error", "Failed to update one or more freebets.");
        this.isLoadingAction = false;
      } finally {
        document.body.style.overflow = "";
      }
    },
    async removeFreebets(item = null) {
      const itemsToDelete = item ? [item] : this.freebetsSelected;

      if (itemsToDelete.length === 0) return;

      const confirm = await Swal.fire({
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        title: this.$t("util.areYouSure"),
        text: item
          ? "This specific freebet will be permanently removed."
          : this.$t("util.actionUndone"),
        confirmButtonText: "Delete",
        cancelButtonText: this.$t("util.cancel"),
        confirmButtonColor: "#ef4444",
      });

      if (!confirm.isConfirmed) return;

      const total = itemsToDelete.length;
      try {
        this.isLoadingAction = true;
        document.body.style.overflow = "hidden";

        for (let i = 0; i < total; i++) {
          this.processedItems = i + 1;
          await deleteRequest(`freebets/${itemsToDelete[i]._id}`, {}, true);
          this.progressIndicator = Math.round(((i + 1) / total) * 100);
        }

        setTimeout(async () => {
          await Swal.fire({
            icon: "success",
            title: this.$t("util.deleteComplete"),
            text:
              total > 1
                ? this.$t("util.allSelectedElementsHaveBeenDeleted")
                : "The freebet has been deleted.",
            timer: 2000,
            showConfirmButton: false,
          });

          this.freebetsSelected = [];
          window.location.reload();
        }, 600);
      } catch (err) {
        console.error("Error deleting freebets:", err);
        Swal.fire("Error", "Failed to delete the selected item(s).");
      } finally {
        this.isLoadingAction = false;
        document.body.style.overflow = "";
      }
    },
    saveDetail(detail) {
      this.freebet_skin_id = detail.skin_id;
      this.freebet_id = detail.external_id;
      this.detailsForRestrictions = detail;
    },
    async resetFilter() {
      this.resetPagination();
      this.external_id = "";
      this.freebet_type = "All";
      this.start_date = this.getInitialTimeStart();
      this.end_date = null;
      this.has_campaign = "All";
      this.is_enabled = "All";
      this.game_id = "All";
      await this.search();
    },

    resetPagination() {
      this.currentPage = 1;
    },
    getInitialTimeStart() {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      d.setHours(0, 0, 0, 0);
      return formatString(String(d), { hours: 0, minutes: 0 });
    },

    formatDateTimeForAPI(val) {
      return new Date(val + "Z");
    },
    onRowSelect() {
      this.freebetsSelected = [...this.freebetsSelected];
    },
    onRowUnselect() {
      this.freebetsSelected = [...this.freebetsSelected];
    },
    onRowSelectAll(event) {
      this.freebetsSelected = [...event.data];
    },
    onRowUnselectAll() {
      this.freebetsSelected = [];
    },
    onPage(event) {
      this.currentPage = event.page + 1;
      const chosen = this.limit.find((l) => l.value === event.rows);
      if (chosen) {
        this.limit.forEach((l) => (l.selected = false));
        chosen.selected = true;
      }
      this.search();
    },

    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder === 1 ? "asc" : "desc";

      this.updateQueryString();
      this.search();
    },

    async search() {
      const params = {
        page: this.currentPage,
        limit: this.selectedLimit,
      };

      if (this.start_date)
        params.start_date = this.formatDateTimeForAPI(this.start_date);
      if (this.end_date)
        params.end_date = this.formatDateTimeForAPI(this.end_date);

      if (this.freebet_type && this.freebet_type.toLowerCase() !== "all") {
        params.type = this.formatFreebetType(this.freebet_type);
      }
      if (this.is_enabled && this.is_enabled.toLowerCase() !== "all") {
        params.is_enabled = this.is_enabled.toLowerCase() !== "false";
      }
      if (this.has_campaign && this.has_campaign.toLowerCase() !== "all") {
        params.hasCampaign = this.has_campaign.toLowerCase() !== "false";
      }
      if (this.external_id && this.external_id !== "") {
        params.external_id = this.external_id.trim();
      }
      if (this.game_id && this.game_id !== "All" && this.isTheearcasino) {
        const key = Object.keys(this.gameIdTheEar).find(
          (k) => this.gameIdTheEar[k] === this.game_id,
        );
        if (key) params.game_id = key.trim();
      }

      if (this.sortField) {
        params.sortField = this.sortField;
        params.sortOrder = this.sortOrder;
      }

      try {
        this.isLoading = true;
        const res = await getFetch("/freebets", params);
        if (res.data?.length > 0) {
          this.tableData = res.data;
          this.totalPages = res.totalPages;
          this.totalElements = res.totalItems;
        } else {
          this.tableData = [];
        }
      } catch (err) {
        console.error("Errore durante il fetch dei dati:", err);
      } finally {
        this.isLoading = false;
      }
    },

    formatFreebetType(val) {
      const map = { ...this.crashGameOptions, ...this.plinkoGameOptions };
      return Object.keys(map).find((k) => map[k] === val) ?? null;
    },
    async checkUrlFilters() {
      const q = this.$route.query;
      if (q.external_id) this.external_id = q.external_id;
      if (q.freebet_type) this.freebet_type = q.freebet_type;
      if (q.start_date) this.start_date = q.start_date;
      if (q.end_date) this.end_date = q.end_date;
      if (q.is_enabled)
        this.is_enabled = q.is_enabled === "true" ? "True" : "False";
      if (q.has_campaign)
        this.has_campaign = q.has_campaign === "true" ? "True" : "False";

      if (q.sortField) {
        this.sortField = q.sortField;
        this.sortOrder = q.sortOrder;
      }
    },

    async checkUrlParams() {
      if (!this.betId) {
        this.start_date = this.getInitialTimeStart();
        this.end_date = null;
        await this.checkUrlFilters();
        await this.search();
      }
    },
  },

  async mounted() {
    import("@/assets/styles/components/swal2ModalDialog.scss");
    await this.checkUrlParams();
  },

  beforeUnmount() {
    document
      .querySelectorAll(
        'link[href*="swal2ModalDialog.scss"], style[data-vue-ssr-id*="swal2ModalDialog.scss"]',
      )
      .forEach((el) => el.remove());
  },

  created() {
    this.setTypeOptionsBasedOnGameType();
  },
};
</script>

<style scoped>
/* ── DataTable tweaks ──────────────────────────────────────────────── */
.freebets-datatable {
  width: 100%;
}

.clickable-cell {
  cursor: pointer;
  color: var(--primary-color, #2563eb);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.clickable-cell:hover {
  opacity: 0.8;
}

/* ── Bulk action bar ───────────────────────────────────────────────── */
.manage-freebets-status {
  display: flex;
  align-items: center;
}

.freebets-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>

<style>
/* ── Transition bulk bar ───────────────────────────────────────────── */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
